import React, { useState, useEffect } from 'react';
import { RefreshCw, CheckCircle2, XCircle, FileJson, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSyncPredictionMutation } from '@/store/api/Predictions/predictionApi';

export function SyncPredictionPage() {
  const [predictionId, setPredictionId] = useState('');
  const [language, setLanguage] = useState('English');
  const [content, setContent] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [audioUrl, setAudioUrl] = useState('');

  const [validationError, setValidationError] = useState<string | null>(null);
  const [result, setResult] = useState<{ type: 'success' | 'error'; messages: string[] } | null>(
    null
  );

  const [syncPrediction, { isLoading }] = useSyncPredictionMutation();

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (validationError) {
      timer = setTimeout(() => {
        setValidationError(null);
      }, 5000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [validationError]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (result) {
      timer = setTimeout(() => {
        setResult(null);
      }, 5000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [result]);

  const handleSync = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);
    setResult(null);

    if (!predictionId.trim()) {
      setValidationError('Prediction ID is required.');
      return;
    }

    if (!language.trim()) {
      setValidationError('Language is required.');
      return;
    }

    let parsedContent: Record<string, any> | undefined = undefined;
    if (content.trim()) {
      let contentToParse = content.trim();
      if (!contentToParse.startsWith('{')) {
        contentToParse = '{' + contentToParse;
      }
      if (!contentToParse.endsWith('}')) {
        contentToParse = contentToParse + '}';
      }

      try {
        parsedContent = JSON.parse(contentToParse);
        if (
          typeof parsedContent !== 'object' ||
          parsedContent === null ||
          Array.isArray(parsedContent)
        ) {
          setValidationError('Content must be a valid JSON object.');
          return;
        }
      } catch (err: any) {
        setValidationError(`Invalid JSON in Content field: ${err.message}`);
        return;
      }
    }

    const payload = {
      predictionId: predictionId.trim(),
      language: language.trim(),
      ...(parsedContent !== undefined && { content: parsedContent }),
      ...(videoUrl.trim() && { videoUrl: videoUrl.trim() }),
      ...(audioUrl.trim() && { audioUrl: audioUrl.trim() }),
    };

    try {
      const res = await syncPrediction(payload).unwrap();
      setResult({
        type: 'success',
        messages: [
          res.message
            ? Array.isArray(res.message)
              ? res.message.join(', ')
              : String(res.message)
            : 'Prediction synced successfully.',
        ],
      });
      setPredictionId('');
      setContent('');
      setVideoUrl('');
      setAudioUrl('');
    } catch (err: any) {
      console.error('Sync Prediction Error:', err);
      let errors: string[] = [];

      if (err?.data?.message) {
        if (Array.isArray(err.data.message)) {
          errors = err.data.message;
        } else {
          errors = [String(err.data.message)];
        }
      } else if (err?.data?.error) {
        errors = [String(err.data.error)];
      } else if (err?.error) {
        errors = [String(err.error)];
      } else {
        errors = ['Failed to sync prediction. Please check your inputs and try again.'];
      }

      setResult({
        type: 'error',
        messages: errors,
      });
    }
  };

  return (
    <div className="h-full bg-background flex items-center justify-center">
      <div className="w-full">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
          {/* Header */}
          <div className="bg-[linear-gradient(to_right,#082261,#34065c)] p-6 text-white">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 rounded-full p-2">
                <RefreshCw className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-xl font-semibold font-spectral">Sync Prediction</h1>
                <p className="text-white/70 text-sm mt-0.5">
                  Synchronize prediction details to the database
                </p>
              </div>
            </div>
          </div>

          {/* Form Body */}
          <form onSubmit={handleSync} className="p-6 space-y-5">
            {/* Prediction ID */}
            <div className="space-y-1.5">
              <Label htmlFor="predictionId" className="text-sm font-medium text-gray-700">
                Prediction ID <span className="text-red-500">*</span>
              </Label>
              <Input
                id="predictionId"
                placeholder="e.g. 6a0c04ad17a5e45ee6d29965"
                value={predictionId}
                onChange={(e) => {
                  setPredictionId(e.target.value);
                  setValidationError(null);
                  setResult(null);
                }}
                className="font-mono text-sm"
                required
              />
            </div>

            {/* Language */}
            <div className="space-y-1.5">
              <Label htmlFor="language" className="text-sm font-medium text-gray-700">
                Language <span className="text-red-500">*</span>
              </Label>
              <Select
                value={language}
                onValueChange={(val) => {
                  setLanguage(val);
                  setValidationError(null);
                  setResult(null);
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select Language" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="English">English</SelectItem>
                  <SelectItem value="Hindi">Hindi</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Content (JSON Object) */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label
                  htmlFor="content"
                  className="text-sm font-medium text-gray-700 flex items-center gap-1.5"
                >
                  <FileJson className="h-4 w-4 text-gray-400" />
                  Content (JSON Object){' '}
                  <span className="text-xs text-gray-400 font-normal">(Optional)</span>
                </Label>
              </div>
              <Textarea
                id="content"
                placeholder='e.g. { "title": "Weekly Horoscope", "overall": 4 }'
                value={content}
                onChange={(e) => {
                  setContent(e.target.value);
                  setValidationError(null);
                  setResult(null);
                }}
                className="font-mono text-xs min-h-[120px]"
              />
            </div>

            {/* Video URL */}
            <div className="space-y-1.5">
              <Label htmlFor="videoUrl" className="text-sm font-medium text-gray-700">
                Video URL <span className="text-xs text-gray-400 font-normal">(Optional)</span>
              </Label>
              <Input
                id="videoUrl"
                type="text"
                placeholder="e.g. https://example.com/video.mp4"
                value={videoUrl}
                onChange={(e) => {
                  setVideoUrl(e.target.value);
                  setValidationError(null);
                  setResult(null);
                }}
                className="text-sm"
              />
            </div>

            {/* Audio URL */}
            <div className="space-y-1.5">
              <Label htmlFor="audioUrl" className="text-sm font-medium text-gray-700">
                Audio URL <span className="text-xs text-gray-400 font-normal">(Optional)</span>
              </Label>
              <Input
                id="audioUrl"
                type="text"
                placeholder="e.g. https://example.com/audio.mp3"
                value={audioUrl}
                onChange={(e) => {
                  setAudioUrl(e.target.value);
                  setValidationError(null);
                  setResult(null);
                }}
                className="text-sm"
              />
            </div>

            {/* Validation Error banner */}
            {validationError && (
              <div className="flex items-start gap-3 rounded-lg p-4 bg-amber-50 border border-amber-200">
                <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-700">{validationError}</p>
              </div>
            )}

            {/* Result banner */}
            {result && (
              <div
                className={`flex items-start gap-3 rounded-lg p-4 border ${
                  result.type === 'success'
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}
              >
                {result.type === 'success' ? (
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 shrink-0" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
                )}
                <div className="text-sm space-y-1">
                  {result.messages.map((msg, idx) => (
                    <p
                      key={idx}
                      className={result.type === 'success' ? 'text-green-700' : 'text-red-700'}
                    >
                      {msg}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={!predictionId.trim() || !language.trim() || isLoading}
              className="
                w-full py-2.5 px-4 rounded-lg font-semibold font-spectral text-sm uppercase
                transition-all duration-300 ease-in-out
                bg-[linear-gradient(to_right,#082261,#34065c)] text-white
                hover:opacity-90 active:scale-[0.99]
                disabled:opacity-40 disabled:cursor-not-allowed disabled:scale-100
                flex items-center justify-center gap-2
              "
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  Sync Prediction
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
